import moment from 'moment-timezone';

import { ACTION_NEEDS_ATTENTION } from '../constants';

import { EMPTY_STATE } from './constants';
import type { ActivityRow, KindFilter, StatusFilter } from './types';

/**
 * Where an undated row sorts: last, and by a finite number.
 *
 * `POSITIVE_INFINITY` reads better and sorts wrong — two undated rows would give the comparator
 * `Infinity - Infinity`, which is `NaN`, and a `NaN` comparator leaves the order between them up
 * to the engine rather than stable.
 */
const UNDATED = Number.MAX_SAFE_INTEGER;

/**
 * When Clive raised the thing, as epoch ms.
 *
 * `created_at` is declared a `Date` and arrives from the API as an ISO string, so it is parsed
 * rather than read — as UTC where it carries no offset, as `serverDateTimeToLocal` reads the
 * server's times, so a recommendation and an ask interleave by the instant each names whichever of
 * them carries an offset. An unreadable date sorts last instead of throwing: a row whose timestamp
 * we cannot parse is still a row the handler may need to act on.
 */
export const raisedAt = (value: Date | string): number => {
  const time = moment.utc(value, moment.ISO_8601).valueOf();

  return Number.isNaN(time) ? UNDATED : time;
};

const STATUS_MATCHES: Record<StatusFilter, (row: ActivityRow) => boolean> = {
  all: () => true,
  needsAttention: (row) => ACTION_NEEDS_ATTENTION[row.state],
  settled: (row) => !ACTION_NEEDS_ATTENTION[row.state],
};

/** The rows both filters leave, in the order they were given. */
export const filterRows = (rows: ActivityRow[], kind: KindFilter, status: StatusFilter): ActivityRow[] =>
  rows.filter((row) => (kind === 'all' || row.kind === kind) && STATUS_MATCHES[status](row));

/**
 * The header's second line, where there is one.
 *
 * Empty at zero: the empty state below the divider already says nothing is waiting, and in nearly
 * these words, so a header saying it too reads as two different statements the reader has to
 * reconcile. The count is the one thing the header adds, and at zero there is no count.
 */
export const attentionSummary = (count: number): string =>
  count ? `${count} need${count === 1 ? 's' : ''} attention` : '';

/**
 * Which of the three empty states this is.
 *
 * Clive having raised nothing says so whatever the filters. Otherwise only the unnarrowed "needs
 * attention" view is the good outcome — nothing is waiting on this handler; any other empty view is
 * a filter that found nothing, and says so.
 */
export const emptyStateMessage = (kind: KindFilter, status: StatusFilter, hasRaisedAnything: boolean): string => {
  if (!hasRaisedAnything) return EMPTY_STATE.nothingRaised;

  return kind === 'all' && status === 'needsAttention' ? EMPTY_STATE.nothingWaiting : EMPTY_STATE.noMatch;
};
